import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { colors, fonts } from '../../theme';

// The start and the finish of a session (owner, 19 October 2026: design 1, "readiness ring").
// Energy is saved as energy_rating 1-3 and effort as rpe 2, 4, 6, 8 or 10, the values the app has
// always saved, so earlier sessions and the coach's charts read the same.
// Each level has its own colour (owner, 20 October 2026). Low energy is a calm blue, not a warning red:
// being tired is not a failure. Effort warms from green to red. All read at 4.5:1 or more on the navy.
export const ENERGY_LEVELS = [
  { value: 1, word: 'Low', color: '#7FA7D9' },
  { value: 2, word: 'Medium', color: colors.gold },
  { value: 3, word: 'High', color: colors.success },
];
export const EFFORT_LEVELS = [
  { value: 2, word: 'Easy', color: colors.success },
  { value: 4, word: 'Steady', color: '#A8C66C' },
  { value: 6, word: 'Challenging', color: colors.gold },
  { value: 8, word: 'Hard', color: '#F5A524' },
  { value: 10, word: 'All out', color: '#FF6B4A' },
];

const RADIUS = 104;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
// An unchosen bar: 3:1 against both the page and the card it sits on
const EMPTY_BAR = '#6B7290';

const MOTION_CSS = `
@keyframes cipherRise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
@keyframes cipherPop { from { opacity: .2; transform: scale(.8); } to { opacity: 1; transform: none; } }
.cipher-rise { animation: cipherRise .45s cubic-bezier(.2,.8,.2,1) both; }
.cipher-pop { animation: cipherPop .35s cubic-bezier(.2,1.4,.4,1) both; }
.cipher-arc { transition: stroke-dashoffset .7s cubic-bezier(.2,.8,.2,1), stroke .4s; }
.cipher-seg span { transition: background-color .3s, height .3s; }
.cipher-cta { transition: transform .15s, background-color .3s, color .3s; }
.cipher-cta:active:not(:disabled) { transform: scale(.98); }
@media (prefers-reduced-motion: reduce) {
  .cipher-rise, .cipher-pop { animation: none; }
  .cipher-arc, .cipher-seg span, .cipher-cta { transition: none; }
}
`;

const kicker = { fontSize: 11, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: colors.textMuted };
const heading = { margin: '8px 0 4px', fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15, color: colors.text };

function Ring({ step, steps, color, label, word, emptyWord }) {
  return (
    <div style={{ position: 'relative', width: 248, height: 248, maxWidth: '70vw', maxHeight: '70vw' }}>
      <svg width="100%" height="100%" viewBox="0 0 248 248" aria-hidden="true" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="124" cy="124" r={RADIUS} fill="none" stroke={colors.surface2} strokeWidth="14" />
        <circle
          className="cipher-arc"
          cx="124" cy="124" r={RADIUS} fill="none" stroke={color} strokeWidth="14" strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE * (1 - step / steps)}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={kicker}>{label}</div>
        {step > 0 ? (
          <div key={step} className="cipher-pop" style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: fonts.mono, fontSize: 56, fontWeight: 600, lineHeight: 1.15, color: colors.text }}>{step}</div>
            <div style={{ fontSize: 16, fontWeight: 600, color }}>{word}</div>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>Level {step} of {steps}</div>
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: fonts.mono, fontSize: 56, fontWeight: 600, lineHeight: 1.15, color: colors.surface3 }}>–</div>
            <div style={{ fontSize: 14, color: colors.textMuted }}>{emptyWord}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// One tappable bar per level; the bars up to the chosen one fill. A radio group: one Tab stop, the arrow
// keys, Home and End move the choice (Codex's review of PR 116, S1)
function Segments({ levels, step, color, name, onPick, disabled }) {
  const group = useRef(null);
  const focusable = step > 0 ? step - 1 : 0;
  const move = (e) => {
    const last = levels.length - 1;
    const from = step > 0 ? step - 1 : -1;
    const to = { ArrowRight: from + 1, ArrowDown: from + 1, ArrowLeft: from - 1, ArrowUp: from - 1, Home: 0, End: last }[e.key];
    if (to === undefined) return;
    e.preventDefault();
    const next = Math.min(last, Math.max(0, to));
    onPick(next + 1);
    group.current?.querySelectorAll('[role="radio"]')[next]?.focus();
  };
  return (
    <div ref={group} role="radiogroup" aria-label={name} onKeyDown={disabled ? undefined : move} style={{ display: 'flex', gap: 8, width: '100%', marginTop: 20 }}>
      {levels.map((level, i) => {
        const chosen = i + 1 === step;
        return (
          <button
            key={level.value}
            type="button"
            role="radio"
            aria-checked={chosen}
            aria-label={level.word}
            tabIndex={i === focusable ? 0 : -1}
            disabled={disabled}
            className="cipher-seg"
            onClick={() => onPick(i + 1)}
            style={{ flex: 1, height: 44, padding: 0, border: 'none', background: 'transparent', cursor: disabled ? 'default' : 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <span style={{ display: 'block', width: '100%', height: chosen ? 10 : 6, borderRadius: 5, background: i < step ? color : EMPTY_BAR }} />
          </button>
        );
      })}
    </div>
  );
}

const ends = (left, right, middle) => (
  <div aria-hidden="true" style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: 12, color: colors.textMuted }}>
    <span>{left}</span>{middle && <span>{middle}</span>}<span>{right}</span>
  </div>
);

function MainButton({ ready, children, onClick, busy }) {
  return (
    <button
      type="button"
      className="cipher-cta"
      onClick={onClick}
      disabled={!ready || busy}
      style={{
        width: '100%', height: 56, border: 'none', borderRadius: 16, fontFamily: fonts.body, fontSize: 16, fontWeight: 600,
        cursor: ready && !busy ? 'pointer' : 'default',
        background: ready ? colors.gold : colors.surface2,
        color: ready ? colors.bg : colors.textMuted,
      }}
    >
      {children}
    </button>
  );
}

const quietLink = {
  width: '100%', minHeight: 44, background: 'none', border: 'none', color: colors.textMuted,
  fontFamily: fonts.body, fontSize: 13, fontWeight: 500, cursor: 'pointer',
};

/**
 * Before a session: the day, what is in it, and the client's energy, which starts the session.
 * onBegin(energyRating 1-3); onSkip() starts it without one.
 */
export function SessionStart({ exerciseNames = [], exerciseSets = [], minutes, onBegin, onSkip }) {
  const [step, setStep] = useState(0);
  // The whole day, to look over before rating (owner, 23 October 2026: the question hid the exercises)
  const [showDay, setShowDay] = useState(false);
  const level = ENERGY_LEVELS[step - 1];
  const more = exerciseNames.length - 3;
  return (
    <section className="cipher-rise" aria-label="Start your session" style={{ display: 'flex', flexDirection: 'column', fontFamily: fonts.body, color: colors.text, padding: '8px 4px 16px' }}>
      <style>{MOTION_CSS}</style>
      <div style={kicker}>Before you start</div>
      <h2 style={heading}>How&rsquo;s your energy?</h2>
      <div style={{ fontSize: 14, color: colors.textMuted, lineHeight: 1.5 }}>
        {exerciseNames.length} exercise{exerciseNames.length === 1 ? '' : 's'}
        {minutes > 0 && `, about ${minutes} min`}
        {exerciseNames.length > 0 && ': '}
        {exerciseNames.slice(0, 3).join(', ')}{more > 0 ? ` and ${more} more` : ''}
      </div>
      {exerciseNames.length > 0 && (
        <button
          type="button"
          onClick={() => setShowDay((v) => !v)}
          aria-expanded={showDay}
          style={{ ...quietLink, width: 'auto', alignSelf: 'flex-start', padding: 0, color: colors.text, textDecoration: 'underline', textUnderlineOffset: 3, textAlign: 'left' }}
        >
          {showDay ? 'Hide today’s exercises' : 'See today’s exercises'}
        </button>
      )}
      {showDay && (
        <ol aria-label="Today’s exercises" style={{ margin: '0 0 4px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {exerciseNames.map((name, i) => (
            <li key={`${name}-${i}`} style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '10px 12px', borderRadius: 10, background: colors.surface1, fontSize: 14 }}>
              <span style={{ color: colors.textMuted, fontVariantNumeric: 'tabular-nums', minWidth: 16 }}>{i + 1}</span>
              <span style={{ flex: 1, color: colors.text }}>{name}</span>
              {exerciseSets[i] > 0 && (
                <span style={{ color: colors.textMuted, fontSize: 13, whiteSpace: 'nowrap' }}>{exerciseSets[i]} set{exerciseSets[i] === 1 ? '' : 's'}</span>
              )}
            </li>
          ))}
        </ol>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '28px auto 0', width: '100%', maxWidth: 360 }}>
        <Ring step={step} steps={3} color={level?.color ?? colors.gold} label="Energy" word={level?.word} emptyWord="Tap a bar" />
        <Segments levels={ENERGY_LEVELS} step={step} color={level?.color ?? colors.gold} name="Your energy today" onPick={setStep} />
        {ends('Low', 'High', 'Medium')}
      </div>

      <div style={{ marginTop: 28, maxWidth: 360, width: '100%', alignSelf: 'center' }}>
        <MainButton ready={step > 0} onClick={() => level && onBegin(level.value)}>
          {step > 0 ? 'Begin session' : 'Choose your energy to begin'}
        </MainButton>
        <button type="button" onClick={onSkip} style={quietLink}>Start without rating</button>
      </div>
    </section>
  );
}

/**
 * After a session: how hard it was, over the whole screen. onSave(rpe 2-10, or null without one);
 * onClose() goes back to the workout. Stays open while saving; the parent closes it once the day is saved.
 */
export function SessionFinish({ onSave, onClose, saving }) {
  const [step, setStep] = useState(0);
  const level = EFFORT_LEVELS[step - 1];
  const color = level?.color ?? colors.textMuted;
  const panel = useRef(null);
  const onCloseRef = useRef(onClose);
  const savingRef = useRef(saving);
  onCloseRef.current = onClose;
  savingRef.current = saving;

  // Once, while open (Codex's review of PR 116, M4 and S2): focus stays inside, the page behind cannot be
  // reached, Escape and the phone's or browser's Back close it (not while saving), and focus goes back to
  // the button that opened it
  useEffect(() => {
    const opener = document.activeElement;
    const app = document.getElementById('root');
    const appHidden = app?.getAttribute('aria-hidden');
    if (app) { app.inert = true; app.setAttribute('aria-hidden', 'true'); }
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.querySelector('button:not([disabled])')?.focus();

    const close = () => { if (!savingRef.current) onCloseRef.current(); };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab' || !panel.current) return;
      const items = [...panel.current.querySelectorAll('button:not([disabled]), [tabindex="0"]')]
        .filter((el) => el.tabIndex >= 0);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!panel.current.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);

    // Back: an extra history entry while open, so Back closes this screen rather than leaving the workout
    let closedByBack = false;
    const onBack = () => {
      if (savingRef.current) { window.history.pushState({ cipherFinish: true }, ''); return; }
      closedByBack = true;
      onCloseRef.current();
    };
    window.history.pushState({ cipherFinish: true }, '');
    window.addEventListener('popstate', onBack);

    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('popstate', onBack);
      if (!closedByBack && window.history.state?.cipherFinish) window.history.back();
      document.body.style.overflow = overflow;
      if (app) {
        app.inert = false;
        if (appHidden === null) app.removeAttribute('aria-hidden'); else app.setAttribute('aria-hidden', appHidden);
      }
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, []);

  return createPortal(
    <div
      ref={panel}
      role="dialog"
      aria-modal="true"
      aria-label="How hard was that?"
      className="cipher-rise"
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: colors.bg, color: colors.text, fontFamily: fonts.body,
        overflowY: 'auto', display: 'flex', justifyContent: 'center',
      }}
    >
      <style>{MOTION_CSS}</style>
      <div style={{ width: '100%', maxWidth: 420, minHeight: '100%', boxSizing: 'border-box', padding: 'max(16px, env(safe-area-inset-top)) 24px max(24px, env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <button type="button" onClick={onClose} disabled={saving} style={{ ...quietLink, width: 'auto', padding: '0 4px', textAlign: 'left' }}>
            ← Back to workout
          </button>
        </div>
        <div style={{ ...kicker, marginTop: 16 }}>Last step</div>
        <h2 style={heading}>How hard was that?</h2>
        <div style={{ fontSize: 14, color: colors.textMuted }}>Your coach uses this to set your next sessions.</div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 32 }}>
          <Ring step={step} steps={5} color={color} label="Effort" word={level?.word} emptyWord="Tap a bar" />
          <Segments levels={EFFORT_LEVELS} step={step} color={color} name="How hard the session was" onPick={setStep} disabled={saving} />
          {ends('Easy', 'All out')}
        </div>

        <div style={{ flex: 1, minHeight: 24 }} />
        <MainButton ready={step > 0} busy={saving} onClick={() => level && onSave(level.value)}>
          {saving ? 'Saving…' : step > 0 ? 'Save and finish' : 'Rate your effort to finish'}
        </MainButton>
        <button type="button" onClick={() => onSave(null)} disabled={saving} style={quietLink}>Finish without rating</button>
      </div>
    </div>,
    document.body
  );
}
